/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import isNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Carousel from "@monsma/Overrides/Components/Carousel";
import { getPaddingMarginSize } from "@monsma/XDL/Utilities";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

interface SlideModel {
    fields: {
        slideTitle: string;
        background: "image" | "color";
        image: string;
        imageOverlay: string;
        partialOverlay: boolean;
        partialOverlayPositioning: "top" | "middle" | "bottom";
        responsiveImageBehavior: "cover" | "center" | "prioritizeHeight" | "prioritizeWidth";
        backgroundColor: string;
        heading: string;
        subheading: string;
        buttonLabel: string;
        buttonLink: LinkFieldValue;
        buttonVariant: "primary" | "secondary" | "tertiary";
        focalPoint:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        contentPadding: string;
        centerTextVertically: boolean;
        customCSS: string;
    };
}

const enum fields {
    height = "height",
    textAlignment = "textAlignment",
    showArrows = "showArrows",
    slideIndicator = "slideIndicator",
    autoplay = "autoplay",
    animationType = "animationType",
    slides = "slides",
    responsiveFontSizes = "responsiveFontSizes",
    customFontSizes = "customFontSizes",
    h1FontSize = "h1FontSize",
    h2FontSize = "h2FontSize",
    h3FontSize = "h3FontSize",
    h4FontSize = "h4FontSize",
    h5FontSize = "h5FontSize",
    h6FontSize = "h6FontSize",
    normalFontSize = "normalFontSize",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.height]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.textAlignment]: "left" | "center" | "right";
        [fields.showArrows]: boolean;
        [fields.slideIndicator]: boolean;
        [fields.autoplay]: number;
        [fields.animationType]: "Slide" | "Fade";
        [fields.responsiveFontSizes]: boolean;
        [fields.customFontSizes]: boolean;
        [fields.h1FontSize]: number;
        [fields.h2FontSize]: number;
        [fields.h3FontSize]: number;
        [fields.h4FontSize]: number;
        [fields.h5FontSize]: number;
        [fields.normalFontSize]: number;
        [fields.slides]: SlideModel[];
        [fields.customCSS]: string;
    };
    extendedStyles?: SlideshowStyles;
}

export interface SlideshowStyles {
    slideShowWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    headingText?: TypographyPresentationProps;
    subheadingText?: TypographyPresentationProps;
}

export const slideshowStyles: SlideshowStyles = {
    slideShowWrapper: {
        css: css`
            &&& {
                position: relative;

                .swiper-navigation {
                    position: absolute;
                    bottom: 40px;
                    right: 15px;
                    margin: 0;
                    z-index: ${getProp("theme.zIndex.default")};

                    > div:nth-child(2) > span {
                        border: 1px solid ${getColor("common.background")};
                        background: transparent;

                        &:hover {
                            background: ${getColor("common.background")};
                        }

                        &[class*="active"] {
                            border: 0;
                            background: ${getColor("common.background")};
                        }
                    }
                }
            }
        `,
    },
    slideContentWrapper: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            position: relative;
            color: ${getColor("text.inverse")};
            background-repeat: no-repeat;
            ${TypographyStyle} {
                color: ${getColor("text.inverse")};
            }
            button {
                ${TypographyStyle} {
                    color: inherit;
                }
            }
        `,
    },
    slideOverlayWrapper: {
        css: css`
            width: 100%;
        `,
    },
    headingText: {
        css: css`
            margin-bottom: 15px;

            > * {
                &:last-child {
                    margin-bottom: 0;
                }
            }
        `,
    },
    subheadingText: {
        css: css`
            margin-bottom: 30px;

            > * {
                &:last-child {
                    margin-bottom: 0;
                }
            }
        `,
    },
};

const defaultCustomCss = `.slideshow-wrapper {
}

.slideshow-carousel {
}

.slideshow-slide-container {
}

.slide-wrapper {
}

.slide-heading {
}

.slide-subheading {
}

.slide-button {
}

.slide-indicator {
}

.slide-dot-button {
}

.slide-arrow-wrapper {
}

.slide-arrow {
}
`;

export const Slideshow = ({ fields, extendedStyles }: OwnProps) => {
    const { isInShell } = useShellContext();
    let heightStyles: string;

    const buttonLinks = useGetLinks(fields.slides, o => o.fields.buttonLink);
    const styles = useMergeStyles("slideshow", slideshowStyles, extendedStyles);
    const BANNER_CAROUSEL_ID = "banner-carousel-id";
    const textAlignStyles = `text-align: ${fields.textAlignment};`;

    const onClick = (link: string | undefined, slide: any) => {
        if (!link) {
            return;
        }

        if (link.startsWith("http")) {
            if (isInShell) {
                return;
            }
            const target = slide.fields.openInNewTab ? "_blank" : "_self";
            window.open(link, target);
        } 
    };

    if (!fields.slides || fields.slides.length === 0) {
        return null;
    }

    switch (fields.height) {
        case "1/4 viewport":
            heightStyles = "min-height: 15vh;";
            break;
        case "1/2 viewport":
            heightStyles = "min-height: 50vh;";
            break;
        case "3/4 viewport":
            heightStyles = "min-height: 75vh;";
            break;
        case "fullViewport":
            heightStyles = "min-height: 100vh;";
            break;
    }

    const mapReviewsToSlides = (slides: any) => {
        return slides?.map((slide: any, index: any) => {
            let responsiveImageBehaviorStyles;
            let overlayPositioningStyles;
            let fontSizeStyles;

            const buttonLink = buttonLinks[index];
            const focalPointStyles = getFocalPointStyles(slide.fields.focalPoint);

            if (slide.fields.responsiveImageBehavior === "cover") {
                responsiveImageBehaviorStyles = "background-size: cover;";
            } else if (slide.fields.responsiveImageBehavior === "center") {
                responsiveImageBehaviorStyles = "background-size: contain;";
            } else if (slide.fields.responsiveImageBehavior === "prioritizeHeight") {
                responsiveImageBehaviorStyles = "background-size: auto 100%;";
            } else if (slide.fields.responsiveImageBehavior === "prioritizeWidth") {
                responsiveImageBehaviorStyles = "background-size: 100% auto;";
            }

            const backgroundStyles =
                slide.fields.background === "image"
                    ? ({ theme }: { theme: BaseTheme }) =>
                          breakpointMediaQueries(theme, [
                              css`
                                  background-image: url(${slide.fields.imageMobile || slide.fields.imageDesktop});
                              `,
                              css`
                                  background-image: url(${slide.fields.imageTablet || slide.fields.imageDesktop});
                              `,
                              css`
                                  background-image: url(${slide.fields.imageTablet || slide.fields.imageDesktop});
                              `,
                              css`
                                  background-image: url(${slide.fields.imageDesktop});
                              `,
                              css`
                                  background-image: url(${slide.fields.imageDesktop});
                              `,
                          ])
                    : `background-color: ${slide.fields.backgroundColor};`;

            if (!slide.fields.partialOverlay) {
                overlayPositioningStyles = "align-items: stretch;";
            } else if (slide.fields.partialOverlayPositioning === "top") {
                overlayPositioningStyles = "align-items: flex-start;";
            } else if (slide.fields.partialOverlayPositioning === "middle") {
                overlayPositioningStyles = "align-items: center;";
            } else if (slide.fields.partialOverlayPositioning === "bottom") {
                overlayPositioningStyles = "align-items: flex-end;";
            }

            if (fields.responsiveFontSizes || fields.customFontSizes) {
                fontSizeStyles = responsiveStyleRules(
                    fields.responsiveFontSizes,
                    fields.customFontSizes ? fields : undefined,
                );
            }

            const slideWrapperStyles = {
                css: css`
                    min-height: 245px;
                    ${styles.slideContentWrapper?.css || ""}
                    ${heightStyles}
                    ${textAlignStyles}
                    ${backgroundStyles}
                    ${responsiveImageBehaviorStyles}
                    ${focalPointStyles}
                    ${overlayPositioningStyles}
                    ${fontSizeStyles}
                `,
            };

            const slideOverlayWrapperStyles = {
                css: css`
                    ${styles.slideOverlayWrapper?.css || ""}
                    background-color: ${slide.fields.background === "image" ? slide.fields.imageOverlay : ""};
                    padding: ${getPaddingMarginSize(slide.fields.contentPadding)};
                    ${
                        slide.fields.centerTextVertically &&
                        `
                            display: flex;
                            align-items: center;
                            justify-content: center;
                        `
                    }
                `,
            };

            const customCssWrapper = {
                css: css`
                    ${fields.customCSS}
                `,
            };
        

            return {
                id: `${BANNER_CAROUSEL_ID}_${index}`,
                // eslint-disable-next-line react/display-name
                renderComponent: () => {
                    return (
                        // eslint-disable-next-line react/no-array-index-key
                        <StyledWrapper key={index} {...slideWrapperStyles}>
                            <StyledWrapper as="span" role="img" aria-label={slide.fields.imageAltText} />
                            <StyledWrapper {...slideOverlayWrapperStyles}>
                                <StyledWrapper>
                                    {slide.fields.heading && (
                                        <StyledWrapper {...styles.headingText}>
                                            {parse(slide.fields.heading, parserOptions)}
                                        </StyledWrapper>
                                    )}
                                    {slide.fields.subheading && (
                                        <StyledWrapper {...styles.subheadingText}>
                                            {parse(slide.fields.subheading, parserOptions)}
                                        </StyledWrapper>
                                    )}
                                    {(slide.fields.buttonLabel || slide.fields.buttonLink.value) && (
                                        <Button
                                            variant={slide.fields.buttonVariant}
                                            onClick={() => onClick(buttonLink?.url, slide)}
                                        >
                                            {slide.fields.buttonLabel || buttonLink?.title || buttonLink?.url}
                                        </Button>
                                    )}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    );
                },
            };
        });
    };

    const slides = mapReviewsToSlides(fields.slides);

    return (
        <StyledWrapper {...styles.slideShowWrapper}>
            <Carousel
                id={BANNER_CAROUSEL_ID}
                maxNumberOfColumns={1}
                showArrows={fields.showArrows}
                showDots={fields.slideIndicator}
                slides={slides}
                autoPlay={fields.autoplay}
                animationType={fields.animationType}
                breakpoints={{
                    0: {
                        slidesPerView: 1,
                    },
                    481: {
                        slidesPerView: 1,
                    },
                    769: {
                        slidesPerView: 1,
                    },
                    1025: {
                        slidesPerView: 1,
                    },
                }}
            />
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Carousel Settings",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: Slideshow,
    definition: {
        group: "Basic",
        icon: "presentation-screen",
        fieldDefinitions: [
            {
                name: fields.height,
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                ],
                defaultValue: "1/4 viewport",
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.textAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "center",
                fieldType: "General",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.showArrows,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.slideIndicator,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.autoplay,
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
                tab: settingsTab,
                min: 1,
            },
            {
                name: fields.animationType,
                editorTemplate: "DropDownField",
                defaultValue: "slide",
                fieldType: "General",
                options: [
                    { displayName: "Slide", value: "slide" },
                    { displayName: "Fade", value: "fade" },
                ],
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.responsiveFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: true,
            },
            {
                name: fields.customFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: false,
            },
            {
                name: fields.normalFontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h1FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: 40,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h2FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: 32,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h3FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h4FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h5FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h6FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.slides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { slideTitle } }) => slideTitle || "Slide",
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "slideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "Slide",
                    },
                    {
                        name: "background",
                        displayName: "Background",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Image", value: "image" },
                            { displayName: "Color", value: "color" },
                        ],
                        defaultValue: "image",
                        hideEmptyOption: true,
                    },
                    {
                        name: "imageDesktop",
                        displayName: "Desktop Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                        isRequired: true,
                    },
                    {
                        name: "imageTablet",
                        displayName: "Tablet Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                        isRequired: false,
                    },
                    {
                        name: "imageMobile",
                        displayName: "Mobile Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                        isRequired: false,
                    },
                    {
                        name: "imageAltText",
                        displayName: "Image Alt Text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                        isRequired: false,
                    },
                    {
                        name: "imageOverlay",
                        displayName: "Image Color Overlay",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "partialOverlay",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "partialOverlayPositioning",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Top", value: "top" },
                            { displayName: "Middle", value: "middle" },
                            { displayName: "Bottom", value: "bottom" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "bottom",
                        isVisible: widget => widget.fields.partialOverlay,
                    },
                    {
                        name: "responsiveImageBehavior",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Cover", value: "cover" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Prioritize height", value: "prioritizeHeight" },
                            { displayName: "Prioritize width", value: "prioritizeWidth" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "cover",
                    },
                    {
                        name: "backgroundColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Color",
                        defaultValue: "black",
                        isVisible: widget => widget.fields.background === "color",
                    },
                    {
                        name: "focalPoint",
                        displayName: "Focal Point",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Top Left", value: "topLeft" },
                            { displayName: "Top Center", value: "topCenter" },
                            { displayName: "Top Right", value: "topRight" },
                            { displayName: "Center Left", value: "centerLeft" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Center Right", value: "centerRight" },
                            { displayName: "Bottom Left", value: "bottomLeft" },
                            { displayName: "Bottom Center", value: "bottomCenter" },
                            { displayName: "Bottom Right", value: "bottomRight" },
                        ],
                        defaultValue: "center",
                        hideEmptyOption: true,
                    },
                    {
                        name: "heading",
                        editorTemplate: "RichTextField",
                        displayName: "Heading",
                        defaultValue: "",
                        extendedConfig: { height: 100 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                    },
                    {
                        name: "subheading",
                        editorTemplate: "RichTextField",
                        displayName: "Subheading",
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                    },
                    {
                        name: "buttonLabel",
                        editorTemplate: "TextField",
                        displayName: "Button Label",
                        defaultValue: "",
                        validate: (value: string | null, item: HasFields) => {
                            if (value && !value.trim()) {
                                return "In valid value.";
                            }

                            const buttonLink = item.fields.buttonLink;

                            if (buttonLink && buttonLink.value && (!value || !value.trim())) {
                                return "A value must be provided.";
                            }

                            return null;
                        },
                        dependentFields: ["buttonLink"],
                    },
                    {
                        name: "buttonLink",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        validate: (link: LinkFieldValue, item: HasFields) => {
                            const buttonLabelValue = item.fields.buttonLabel;

                            if (buttonLabelValue && buttonLabelValue.trim() && (!link || !link.value)) {
                                return "A value must be provided.";
                            }

                            return null;
                        },
                        dependentFields: ["buttonLabel"],
                    },
                    {
                        name: "openInNewTab",
                        displayName: "Open In New Tab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "buttonVariant",
                        displayName: "Button Variant",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Primary", value: "primary" },
                            { displayName: "Secondary", value: "secondary" },
                            { displayName: "Tertiary", value: "tertiary" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "primary",
                    },
                    {
                        name: "contentPadding",
                        editorTemplate: "TextField",
                        defaultValue: "20",
                        isRequired: true,
                        placeholder: "Enter: 5 or '5, 6' or '5, 6, 7, 8'",
                        validate: (value: string | null, item: HasFields) => {
                            if (value && value.split(",").length > 4) {
                                return `Invalid Input`;
                            }
                            if (value && isNumeric(value) && parseFloat(value) < 0) {
                                return `Invalid Input`;
                            }
                            return null;
                        },
                    },
                    {
                        name: "centerTextVertically",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
